import * as React from 'react'
import { cn } from '@/lib/utils'

type ToastVariant = 'success' | 'error' | 'info'

interface ToastItem {
  id: string
  title: string
  description?: string
  variant: ToastVariant
}

interface ToastInput {
  title: string
  description?: string
  variant?: ToastVariant
  durationMs?: number
}

interface ToastContextValue {
  toast: (t: ToastInput) => string
  dismiss: (id: string) => void
}

const ToastContext = React.createContext<ToastContextValue | null>(null)

const variantStyles: Record<ToastVariant, { ring: string; role: 'status' | 'alert' }> = {
  success: { ring: 'bg-success', role: 'status' },
  error: { ring: 'bg-destructive', role: 'alert' },
  info: { ring: 'bg-info', role: 'status' },
}

const icon: Record<ToastVariant, string> = { success: '✓', error: '!', info: 'i' }

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([])
  const timers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const counter = React.useRef(0)

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    if (timers.current[id]) {
      clearTimeout(timers.current[id])
      delete timers.current[id]
    }
  }, [])

  const toast = React.useCallback(
    ({ title, description, variant = 'info', durationMs = 5000 }: ToastInput) => {
      counter.current += 1
      const id = `toast-${counter.current}`
      setToasts((prev) => [...prev, { id, title, description, variant }])
      timers.current[id] = setTimeout(() => dismiss(id), durationMs)
      return id
    },
    [dismiss]
  )

  React.useEffect(() => {
    const t = timers.current
    return () => { Object.values(t).forEach(clearTimeout) }
  }, [])

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            role={variantStyles[t.variant].role}
            className="animate-slide-up flex items-start gap-3 rounded-xl border border-border bg-card p-3.5 shadow-lg"
          >
            <span className={cn('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white', variantStyles[t.variant].ring)} aria-hidden="true">
              {icon[t.variant]}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground">{t.title}</div>
              {t.description && <div className="mt-0.5 text-xs text-muted-foreground">{t.description}</div>}
            </div>
            <button onClick={() => dismiss(t.id)} aria-label="Dismiss" className="shrink-0 text-muted-foreground hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">✕</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}
